/**
 * ALTIORA · ODE · Admin config (Sprint 6 · Founder only)
 *
 * Manage :
 *   • Poids du scoring
 *   • Canaux et seuils d'urgence
 *   • Flux ICS (chambres, universités, associations)
 *   • Sources économiques (data.gouv Québec, Canada Open Data)
 *
 * Une seule PATCH /api/ode/config persiste l'ensemble.
 */
import { useEffect, useState, useCallback } from "react";
import { Save, Plus, Trash2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PageShell, PageHeader, Card, Section, PrimaryButton, GhostButton,
  DangerButton, FormField, Input, Loader, TabsShell, Alert, Checkbox,
} from "@/components/altiora/PremiumUI";
import { useUserLocation } from "@/hooks/useUserLocation";


export default function AdminOdeConfig() {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [cfg,     setCfg]     = useState(null);
  const [tab,     setTab]     = useState("radars");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ode/config");
      setCfg(data);
    } catch { toast.error("Chargement impossible"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/ode/config", cfg);
      setCfg(data);
      toast.success("Configuration ODE enregistrée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sauvegarde impossible");
    } finally { setSaving(false); }
  };

  return (
    <PageShell testid="admin-ode-config-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <PageHeader
          eyebrow="Fondateur · Configuration"
          title="ODE · Configuration"
          subtitle="Ajustez les poids IA, les canaux et les sources publiques que les radars consultent. Toutes les modifications s'appliquent immédiatement."
          testid="admin-ode-header"
          actions={
            <>
              <GhostButton onClick={load} testid="admin-ode-refresh">
                <RefreshCw size={13} /> Recharger
              </GhostButton>
              <PrimaryButton onClick={save} disabled={saving}
                             testid="admin-ode-save">
                <Save size={13} /> {saving ? "Enregistrement…" : "Enregistrer"}
              </PrimaryButton>
            </>
          }
        />
        {loading && <div className="py-16 flex justify-center"><Loader /></div>}
        {!loading && cfg && (
          <>
            <TabsShell
              testid="admin-ode-tabs"
              tabs={[
                { id: "radars",  label: "Sources & Radars" },
                { id: "scoring", label: "Poids IA" },
                { id: "meta",    label: "Général" },
              ]}
              active={tab}
              onChange={setTab}
            />
            {tab === "radars"  && <RadarsTab   cfg={cfg} setCfg={setCfg} />}
            {tab === "scoring" && <ScoringTab  cfg={cfg} setCfg={setCfg} />}
            {tab === "meta"    && <MetaTab     cfg={cfg} setCfg={setCfg} />}
          </>
        )}
      </div>
    </PageShell>
  );
}


// ── Radars & sources ───────────────────────────────────────────────

function RadarsTab({ cfg, setCfg }) {
  const { city: userCity } = useUserLocation();
  const feedLabelHint = userCity ? `Chambre ${userCity}` : "Chambre locale";
  const setFeeds   = (v) => setCfg({ ...cfg, ics_feeds: v });
  const setSources = (v) => setCfg({ ...cfg, economic_sources: v });

  const addFeed = () => setFeeds([...(cfg.ics_feeds || []),
                                    { url: "", label: "" }]);
  const updFeed = (i, k, v) => setFeeds(
    cfg.ics_feeds.map((f, idx) => idx === i ? { ...f, [k]: v } : f));
  const rmFeed = (i) => setFeeds(cfg.ics_feeds.filter((_, idx) => idx !== i));

  const addSrc = () => setSources([...(cfg.economic_sources || []),
                                       { url: "", label: "", country: "CA" }]);
  const updSrc = (i, k, v) => setSources(
    cfg.economic_sources.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const rmSrc = (i) => setSources(cfg.economic_sources.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-5">
      <Card className="p-4" testid="admin-ode-feeds-card">
        <Section title="Flux ICS · Événements de réseautage"
                 hint="Chambres de commerce, universités, incubateurs — URL de flux .ics public.">
          <Alert variant="info" testid="admin-ode-feeds-hint">
            Le radar réseautage ingère les événements &lt; 60 jours. Ajoutez uniquement
            des URL publiquement partagées par les organisations. Aucune donnée privée n'est jamais collectée.
          </Alert>
          <div className="space-y-2 mt-3">
            {(cfg.ics_feeds || []).map((f, i) => (
              <div key={i} data-testid={`admin-ode-feed-${i}`}
                   className="grid grid-cols-1 sm:grid-cols-6 gap-2 items-end
                                rounded-lg p-3 bg-white"
                   style={{ border: "1px solid #C5A88033" }}>
                <FormField label="Libellé" testid={`admin-ode-feed-label-${i}`}>
                  {({ id }) => (
                    <Input id={id} value={f.label} placeholder={feedLabelHint}
                           onChange={(e) => updFeed(i, "label", e.target.value)}
                           testid={`admin-ode-feed-label-input-${i}`} />
                  )}
                </FormField>
                <div className="sm:col-span-4">
                  <FormField label="URL du flux .ics" testid={`admin-ode-feed-url-${i}`}>
                    {({ id }) => (
                      <Input id={id} value={f.url}
                             placeholder="https://cc.example.ca/events.ics"
                             onChange={(e) => updFeed(i, "url", e.target.value)}
                             testid={`admin-ode-feed-url-input-${i}`} />
                    )}
                  </FormField>
                </div>
                <div>
                  <DangerButton onClick={() => rmFeed(i)}
                                testid={`admin-ode-feed-remove-${i}`}>
                    <Trash2 size={12} />
                  </DangerButton>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <GhostButton onClick={addFeed} testid="admin-ode-feed-add">
              <Plus size={13} /> Ajouter un flux ICS
            </GhostButton>
          </div>
        </Section>
      </Card>

      <Card className="p-4" testid="admin-ode-sources-card">
        <Section title="Sources économiques · data.gouv & Open Data"
                 hint="Endpoints CKAN publics (Québec + Canada) qui listent programmes, subventions et crédits d'impôt.">
          <div className="space-y-2">
            {(cfg.economic_sources || []).map((s, i) => (
              <div key={i} data-testid={`admin-ode-src-${i}`}
                   className="grid grid-cols-1 sm:grid-cols-7 gap-2 items-end
                                rounded-lg p-3 bg-white"
                   style={{ border: "1px solid #C5A88033" }}>
                <FormField label="Libellé" testid={`admin-ode-src-label-${i}`}>
                  {({ id }) => (
                    <Input id={id} value={s.label}
                           placeholder="data.gouv.qc.ca"
                           onChange={(e) => updSrc(i, "label", e.target.value)}
                           testid={`admin-ode-src-label-input-${i}`} />
                  )}
                </FormField>
                <div className="sm:col-span-4">
                  <FormField label="Endpoint API JSON" testid={`admin-ode-src-url-${i}`}>
                    {({ id }) => (
                      <Input id={id} value={s.url}
                             placeholder="https://www.donneesquebec.ca/recherche/api/3/action/datastore_search?..."
                             onChange={(e) => updSrc(i, "url", e.target.value)}
                             testid={`admin-ode-src-url-input-${i}`} />
                    )}
                  </FormField>
                </div>
                <FormField label="Pays" testid={`admin-ode-src-country-${i}`}>
                  {({ id }) => (
                    <Input id={id} value={s.country || "CA"} maxLength={2}
                           onChange={(e) => updSrc(i, "country", e.target.value.toUpperCase())}
                           testid={`admin-ode-src-country-input-${i}`} />
                  )}
                </FormField>
                <div>
                  <DangerButton onClick={() => rmSrc(i)}
                                testid={`admin-ode-src-remove-${i}`}>
                    <Trash2 size={12} />
                  </DangerButton>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <GhostButton onClick={addSrc} testid="admin-ode-src-add">
              <Plus size={13} /> Ajouter une source économique
            </GhostButton>
          </div>
        </Section>
      </Card>
    </div>
  );
}


// ── Scoring weights ────────────────────────────────────────────────

function ScoringTab({ cfg, setCfg }) {
  const w = cfg.weights || {};
  const setW = (k, v) => setCfg({ ...cfg, weights: { ...w,
    [k]: parseInt(v, 10) || 0 } });
  const total = Object.values(w).reduce((s, v) => s + (v || 0), 0);

  const rows = [
    ["economic_potential", "Potentiel économique"],
    ["proximity",          "Proximité géographique"],
    ["fit_specialty",      "Adéquation métier"],
    ["urgency",            "Urgence"],
    ["conversion_prob",    "Probabilité de conversion"],
    ["history_signal",     "Signal historique"],
    ["source_quality",     "Qualité de la source"],
    ["contact_ease",       "Facilité de contact"],
  ];

  return (
    <Card className="p-4" testid="admin-ode-scoring-card">
      <Section title="Poids du scoring IA"
               hint={`Total actuel : ${total} (idéalement proche de 100)`}>
        <Alert variant={Math.abs(total - 100) < 15 ? "info" : "warning"}
               title="Répartition des poids"
               testid="admin-ode-weights-alert">
          Chaque facteur contribue au score final entre 0 et 100. Une répartition
          proche de 100 au total garantit une échelle stable — mais toute somme est acceptée.
        </Alert>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          {rows.map(([k, label]) => (
            <FormField key={k} label={label} testid={`admin-ode-weight-${k}`}>
              {({ id }) => (
                <Input id={id} type="number" min="0" max="100"
                       value={w[k] ?? 0}
                       onChange={(e) => setW(k, e.target.value)}
                       testid={`admin-ode-weight-input-${k}`} />
              )}
            </FormField>
          ))}
        </div>
      </Section>
    </Card>
  );
}


// ── Meta / general ─────────────────────────────────────────────────

function MetaTab({ cfg, setCfg }) {
  return (
    <Card className="p-4" testid="admin-ode-meta-card">
      <Section title="Réglages généraux">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <FormField label="Top N quotidien" testid="admin-ode-topn">
            {({ id }) => (
              <Input id={id} type="number" min="1" max="20"
                     value={cfg.daily_top_n}
                     onChange={(e) => setCfg({ ...cfg,
                       daily_top_n: parseInt(e.target.value, 10) || 5 })}
                     testid="admin-ode-topn-input" />
            )}
          </FormField>
          <FormField label="Horizon dashboard (jours)" testid="admin-ode-horizon">
            {({ id }) => (
              <Input id={id} type="number" min="1" max="90"
                     value={cfg.dashboard_horizon_days}
                     onChange={(e) => setCfg({ ...cfg,
                       dashboard_horizon_days: parseInt(e.target.value, 10) || 14 })}
                     testid="admin-ode-horizon-input" />
            )}
          </FormField>
          <div className="flex items-center pt-6">
            <Checkbox label="Coach IA activé (Claude)"
                      checked={cfg.ai_enabled}
                      onChange={(e) => setCfg({ ...cfg, ai_enabled: e.target.checked })}
                      testid="admin-ode-ai-enabled" />
          </div>
        </div>
      </Section>
    </Card>
  );
}
